import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  titleId: string;
  describedById?: string;
  /** Called for Escape and the close button. Ignored while `busy`. */
  onClose: () => void;
  busy?: boolean;
  closeLabel?: string;
  /** Show the top-right close (×) button, as in the Figma cards. */
  showClose?: boolean;
  children: ReactNode;
}

/**
 * The EPN-231 dialog shell: renders above the page, traps Tab inside, closes with Escape, focuses the first control (or the one marked
 * `data-autofocus`) and returns focus to what had it before. The caller owns what each button does.
 */
export function Modal({ titleId, describedById, onClose, busy = false, closeLabel = 'Close', showClose = true, children }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const target = node?.querySelector<HTMLElement>('[data-autofocus]') ?? node?.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus();
    return () => previous?.focus?.();
  }, []);

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape' && !busy) {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div className="wc-backdrop" role="presentation">
      <div ref={ref} className="wc-modal" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={describedById} onKeyDown={onKeyDown}>
        {showClose && (
          <button type="button" className="wc-modal__close" aria-label={closeLabel} onClick={onClose} disabled={busy}>
            ×
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
